import { useEffect, useCallback, useRef } from 'react'
import { useLocalStorage } from '@/hooks/useLocalStorage'

// ─── Types ────────────────────────────────────────────────────────────────────

export type ShortcutId =
  | 'go-live'
  | 'create-clip'
  | 'toggle-mute'
  | 'toggle-brb'
  | 'run-poll'
  | 'run-ad'
  | 'command-palette'
  | 'show-shortcuts'
  | 'sidebar-1'
  | 'sidebar-2'
  | 'sidebar-3'
  | 'sidebar-4'
  | 'sidebar-5'
  | 'sidebar-6'
  | 'sidebar-7'
  | 'sidebar-8'
  | 'sidebar-9'

export interface ShortcutBinding {
  id: ShortcutId
  label: string
  description: string
  category: 'stream' | 'navigation' | 'chat' | 'general'
  /** Serialized as "Ctrl+Shift+G", "?", etc. */
  combo: string
  /** Default combo for reset */
  defaultCombo: string
}

// ─── Default bindings ─────────────────────────────────────────────────────────

export const DEFAULT_SHORTCUTS: ShortcutBinding[] = [
  {
    id: 'go-live',
    label: 'Go Live / End Stream',
    description: 'Toggle your stream on/off',
    category: 'stream',
    combo: 'Ctrl+Shift+G',
    defaultCombo: 'Ctrl+Shift+G',
  },
  {
    id: 'create-clip',
    label: 'Create Clip',
    description: 'Save a clip of your recent broadcast',
    category: 'stream',
    combo: 'Ctrl+Shift+C',
    defaultCombo: 'Ctrl+Shift+C',
  },
  {
    id: 'toggle-mute',
    label: 'Toggle Mute',
    description: 'Mute/unmute your microphone',
    category: 'stream',
    combo: 'Ctrl+Shift+M',
    defaultCombo: 'Ctrl+Shift+M',
  },
  {
    id: 'toggle-brb',
    label: 'Toggle BRB Scene',
    description: 'Switch to/from the BRB scene',
    category: 'stream',
    combo: 'Ctrl+Shift+B',
    defaultCombo: 'Ctrl+Shift+B',
  },
  {
    id: 'run-poll',
    label: 'Run Poll',
    description: 'Start a quick channel poll',
    category: 'stream',
    combo: 'Ctrl+Shift+P',
    defaultCombo: 'Ctrl+Shift+P',
  },
  {
    id: 'run-ad',
    label: 'Run Ad',
    description: 'Trigger an ad break',
    category: 'stream',
    combo: 'Ctrl+Shift+A',
    defaultCombo: 'Ctrl+Shift+A',
  },
  {
    id: 'command-palette',
    label: 'Command Palette',
    description: 'Search pages, actions, and commands',
    category: 'navigation',
    combo: 'Ctrl+K',
    defaultCombo: 'Ctrl+K',
  },
  {
    id: 'show-shortcuts',
    label: 'Show Shortcuts',
    description: 'Display this shortcuts reference',
    category: 'general',
    combo: '?',
    defaultCombo: '?',
  },
  {
    id: 'sidebar-1',
    label: 'Sidebar Item 1',
    description: 'Jump to the first sidebar item',
    category: 'navigation',
    combo: '1',
    defaultCombo: '1',
  },
  {
    id: 'sidebar-2',
    label: 'Sidebar Item 2',
    description: 'Jump to the second sidebar item',
    category: 'navigation',
    combo: '2',
    defaultCombo: '2',
  },
  {
    id: 'sidebar-3',
    label: 'Sidebar Item 3',
    description: 'Jump to the third sidebar item',
    category: 'navigation',
    combo: '3',
    defaultCombo: '3',
  },
  {
    id: 'sidebar-4',
    label: 'Sidebar Item 4',
    description: 'Jump to the fourth sidebar item',
    category: 'navigation',
    combo: '4',
    defaultCombo: '4',
  },
  {
    id: 'sidebar-5',
    label: 'Sidebar Item 5',
    description: 'Jump to the fifth sidebar item',
    category: 'navigation',
    combo: '5',
    defaultCombo: '5',
  },
  {
    id: 'sidebar-6',
    label: 'Sidebar Item 6',
    description: 'Jump to the sixth sidebar item',
    category: 'navigation',
    combo: '6',
    defaultCombo: '6',
  },
  {
    id: 'sidebar-7',
    label: 'Sidebar Item 7',
    description: 'Jump to the seventh sidebar item',
    category: 'navigation',
    combo: '7',
    defaultCombo: '7',
  },
  {
    id: 'sidebar-8',
    label: 'Sidebar Item 8',
    description: 'Jump to the eighth sidebar item',
    category: 'navigation',
    combo: '8',
    defaultCombo: '8',
  },
  {
    id: 'sidebar-9',
    label: 'Sidebar Item 9',
    description: 'Jump to the ninth sidebar item',
    category: 'navigation',
    combo: '9',
    defaultCombo: '9',
  },
]

// ─── LocalStorage key ─────────────────────────────────────────────────────────

export const SHORTCUTS_STORAGE_KEY = 'kast:keyboard-shortcuts'

// ─── Combo parsing ────────────────────────────────────────────────────────────

/**
 * Normalize a KeyboardEvent into a combo string like "Ctrl+Shift+G" or "?".
 * Ctrl and Cmd are treated as the same modifier.
 */
export function eventToCombo(e: KeyboardEvent): string {
  const parts: string[] = []
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl')
  if (e.shiftKey) parts.push('Shift')
  if (e.altKey) parts.push('Alt')

  const ignoredModifierKeys = new Set(['Control', 'Meta', 'Shift', 'Alt'])
  if (!ignoredModifierKeys.has(e.key)) {
    // Normalize single printable chars to uppercase; keep named keys (e.g. Escape) as-is
    const key = e.key.length === 1 ? e.key.toUpperCase() : e.key
    parts.push(key)
  }

  return parts.join('+')
}

/**
 * True when focus is inside a text-entry element where global shortcuts
 * should be suppressed.
 */
function isTextTarget(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null
  if (!target) return false
  const tag = target.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if (target.isContentEditable) return true
  return false
}

// ─── Handlers map ─────────────────────────────────────────────────────────────

export type ShortcutHandlers = Partial<Record<ShortcutId, () => void>>

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Mount global keyboard shortcut handling.
 *
 * @param handlers  Map of shortcut id → callback. Only mounted when the
 *                  corresponding handler is provided; others are ignored.
 * @param enabled   Set to false to temporarily disable all shortcuts
 *                  (e.g. while a modal is open that handles its own keys).
 */
export function useKeyboardShortcuts(
  handlers: ShortcutHandlers,
  enabled = true,
): {
  shortcuts: ShortcutBinding[]
  setShortcuts: (value: ShortcutBinding[] | ((prev: ShortcutBinding[]) => ShortcutBinding[])) => void
  resetToDefaults: () => void
} {
  const [shortcuts, setShortcuts] = useLocalStorage<ShortcutBinding[]>(
    SHORTCUTS_STORAGE_KEY,
    DEFAULT_SHORTCUTS,
  )

  // Stable ref so the event listener never becomes stale
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  const shortcutsRef = useRef(shortcuts)
  shortcutsRef.current = shortcuts

  const resetToDefaults = useCallback(() => {
    setShortcuts(DEFAULT_SHORTCUTS)
  }, [setShortcuts])

  useEffect(() => {
    if (!enabled) return

    function handleKeyDown(e: KeyboardEvent) {
      // Never fire inside text inputs (except Escape which is fine)
      if (e.key !== 'Escape' && isTextTarget(e)) return

      const combo = eventToCombo(e)

      // Find first matching binding
      const binding = shortcutsRef.current.find((b) => b.combo === combo)
      if (!binding) return

      const handler = handlersRef.current[binding.id]
      if (!handler) return

      // Prevent browser defaults (e.g. Ctrl+K opens address bar)
      e.preventDefault()
      handler()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [enabled])

  return { shortcuts, setShortcuts, resetToDefaults }
}
